import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import chesiLogo from '../chesi-logo.png';
import barcodeLabelsImage from '../barcode-labels.png';
import api from '../services/api';
import ProductCard from '../components/shop/ProductCard';
import ShopBannerCarousel from '../components/shop/ShopBannerCarousel';
import ShopHeaderSearch from '../components/shop/ShopHeaderSearch';
import { useCart } from '../context/CartContext';
import { mapApiProductToCard, getApiOrigin, resolveImageUrl } from '../utils/shopHelpers';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PrintIcon from '@mui/icons-material/Print';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import TabletIcon from '@mui/icons-material/Tablet';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LabelIcon from '@mui/icons-material/Label';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import './Shop.css';
import Seo, { buildItemListSchema } from '../components/seo/Seo';
import { PAGE_SEO } from '../config/seo';

/** MUI fallback when CMS has no icon_url for this slug */
const FALLBACK_ICON_BY_SLUG = {
  'handheld-computers': PhoneAndroidIcon,
  'barcode-printers': PrintIcon,
  'barcode-scanners': QrCodeScannerIcon,
  'tablet-computers': TabletIcon,
  'receipt-printers': ReceiptIcon,
  'barcode-labels': LabelIcon,
  'rfid-readers': SignalCellularAltIcon,
  'autonomous-mobile-robotics': SmartToyIcon
};

function shopImageBase() {
  return getApiOrigin();
}

const Shop = () => {
  const { cartCount } = useCart();
  const [topCategories, setTopCategories] = useState([]);
  const [categoriesError, setCategoriesError] = useState('');
  const [brands, setBrands] = useState([]);
  const [brandsError, setBrandsError] = useState('');
  const [bestSellers, setBestSellers] = useState([]);
  const [customerFavorites, setCustomerFavorites] = useState([]);
  const [shopProductsError, setShopProductsError] = useState('');
  const [shopBanners, setShopBanners] = useState([]);
  const [bannersLoading, setBannersLoading] = useState(true);
  const [clientLogos, setClientLogos] = useState([]);
  const [clientLogosLoading, setClientLogosLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getPublicShopBanners();
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) {
          setShopBanners(list);
        }
      } catch {
        if (!cancelled) setShopBanners([]);
      } finally {
        if (!cancelled) setBannersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getPublicClientLogos();
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) setClientLogos(list);
      } catch {
        if (!cancelled) setClientLogos([]);
      } finally {
        if (!cancelled) setClientLogosLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getPublicShopCategories();
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) {
          setTopCategories(list);
          setCategoriesError('');
        }
      } catch (e) {
        if (!cancelled) {
          setTopCategories([]);
          setCategoriesError(e.message || 'Could not load categories');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getPublicShopBrands();
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) {
          setBrands(list);
          setBrandsError('');
        }
      } catch (e) {
        if (!cancelled) {
          setBrands([]);
          setBrandsError(e.message || 'Could not load brands');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setShopProductsError('');
        const res = await api.getPublicShopProducts({ limit: 36, offset: 0, featured: false });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const mapped = rows.map(mapApiProductToCard);
        if (!cancelled) {
          setBestSellers(mapped.slice(0, 6));
          setCustomerFavorites(mapped.slice(6, 18));
        }
      } catch (e) {
        if (!cancelled) {
          setBestSellers([]);
          setCustomerFavorites([]);
          setShopProductsError(e.message || 'Could not load products');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="shop-page">
      <Seo
        {...PAGE_SEO.shop}
        jsonLd={
          bestSellers.length
            ? buildItemListSchema(bestSellers, 'Best Selling AIDC Products')
            : null
        }
      />
      {/* Header */}
      <header className="shop-header">
        <div className="header-top">
          <div className="container">
            <div className="header-content">
              <Link to="/" className="logo">
                <img src={chesiLogo} alt="Chesi" className="logo-img" />
                
              </Link>
              <ShopHeaderSearch
                inputClassName="search-input"
                buttonClassName="search-btn"
              />
              <div className="header-links">
                <Link to="/login" className="header-link">LOGIN</Link>
                <Link to="/account" className="header-link">MY ACCOUNT</Link>
                <Link to="/cart" className="header-link">CART ({cartCount})</Link>
                
                <a href="tel:+254 727 450523" className="header-link">Sales: +254 727 450523</a>
              </div>
            </div>
          </div>
        </div>
        <nav className="shop-nav">
          <div className="container">
            <ul className="nav-menu">
              <li><a href="#">Labels & Supplies</a></li>
              <li><a href="#">Mobile Computing</a></li>
              <li><a href="#">Barcode Scanning</a></li>
              <li><a href="#">Barcode Printing</a></li>
              <li><a href="#">ID Card Printing</a></li>
              <li><a href="#">POS & Touch Signage</a></li>
              <li><a href="#">Wireless Networking</a></li>
              <li><a href="#">RFID</a></li>
              <li><a href="#">Services</a></li>
              <li><a href="#">Brands</a></li>
            </ul>
          </div>
        </nav>
      </header>

      {/* Hero carousel — fixed height, managed in Portal CMS → Banners */}
      <ShopBannerCarousel banners={shopBanners} loading={bannersLoading} />

      {/* Guarantees Section */}
      <section className="guarantees-section">
        <div className="container">
          <div className="guarantees-grid">
            <div className="guarantee-item">
              <div className="guarantee-icon">
                <AttachMoneyIcon sx={{ fontSize: 24, color: '#000' }} />
              </div>
              <h3>Price Match Guarantee</h3>
              <p>Within 30 days of your purchase</p>
            </div>
            <div className="guarantee-item">
              <div className="guarantee-icon">
                <BusinessCenterIcon sx={{ fontSize: 24, color: '#000' }} />
              </div>
              <h3>Sales Expertise Guarantee</h3>
              <p>Industry experts are standing by to answer your questions</p>
            </div>
            <div className="guarantee-item">
              <div className="guarantee-icon">
                <LocalShippingIcon sx={{ fontSize: 24, color: '#000' }} />
              </div>
              <h3>Same Day Delivery Guarantee</h3>
              <p>If it's in stock, it will ship same day</p>
            </div>
          </div>
        </div>
      </section>

      {/* Shop Top Categories */}
      <section className="categories-section">
        <div className="container">
          <h2 className="section-title">Shop Top Categories</h2>
          {categoriesError ? (
            <p className="category-hint" style={{ color: '#b45309', marginBottom: 12 }}>
              {categoriesError} — ensure ChesiPortal_API is running and categories are enabled with no parent in the Portal.
            </p>
          ) : null}
          <div className="categories-grid">
            {topCategories.length === 0 && !categoriesError ? (
              <p className="category-hint" style={{ gridColumn: '1 / -1' }}>
                No published categories yet. Add top-level categories in Chesi Portal (CMS → Categories) and mark them enabled.
              </p>
            ) : (
              topCategories.map((category) => {
                const IconComponent = FALLBACK_ICON_BY_SLUG[category.slug] || PhoneAndroidIcon;
                const origin = shopImageBase();
                const raw = category.icon_url;
                const imgSrc =
                  raw && /^https?:\/\//i.test(raw)
                    ? raw
                    : raw
                      ? `${origin}${raw.startsWith('/') ? '' : '/'}${raw}`
                      : null;
                return (
                  <Link key={category.id} to={`/shop/category/${category.slug}`} className="category-item">
                    <div className="category-icon">
                      {imgSrc ? (
                        <img src={imgSrc} alt="" style={{ maxWidth: 48, maxHeight: 48, objectFit: 'contain' }} />
                      ) : (
                        <IconComponent sx={{ fontSize: 48, color: '#000' }} />
                      )}
                    </div>
                    <p className="category-name">{category.name}</p>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* Best Sellers */}
      <section className="products-section best-sellers-section">
        <div className="container">
          <h2 className="section-title">Best Sellers</h2>
          {shopProductsError ? (
            <p className="category-hint" style={{ color: '#b45309', marginBottom: 12 }}>
              {shopProductsError} — ensure ChesiPortal_API is running and products are published with an enabled category.
            </p>
          ) : null}
          <div className="products-grid">
            {bestSellers.length === 0 && !shopProductsError ? (
              <p className="category-hint" style={{ gridColumn: '1 / -1' }}>
                No published products yet. Add products in Chesi Portal (CMS → Products) and set status to Published.
              </p>
            ) : (
              bestSellers.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))
            )}
          </div>
        </div>
      </section>

      {/* Easy Label Finder Banner */}
      <section className="label-finder-banner">
        <div className="container">
          <div className="banner-content">
            <div className="banner-text">
              <h2>Find the right barcode labels for your printer.</h2>
              <p>Most Labels Ship Same Day.</p>
              <button className="btn-banner">Try our Label Finder</button>
            </div>
            <div className="banner-image">
              <img
                src={barcodeLabelsImage}
                alt="Thermal barcode labels and printer ribbons"
                className="banner-image__labels"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="brands-section">
        <div className="container">
          <div className="brands-header">
            <h2 className="section-title">brands</h2>
            <a href="/shop/brands" className="link-more">SHOP BY BRAND &gt;</a>
          </div>
          {brandsError ? (
            <p className="category-hint" style={{ color: '#b45309', marginBottom: 12 }}>
              {brandsError} — ensure ChesiPortal_API is running and brands are enabled in Chesi Portal.
            </p>
          ) : null}
          <div className="brands-grid">
            {brands.length === 0 && !brandsError ? (
              <p className="category-hint" style={{ gridColumn: '1 / -1' }}>
                No enabled brands found. Create brands in Chesi Portal (CMS → Brands) and keep them enabled.
              </p>
            ) : (
              brands.map((brand) => {
                const origin = shopImageBase();
                const raw = brand.logo_url;
                const imgSrc =
                  raw && /^https?:\/\//i.test(raw)
                    ? raw
                    : raw
                      ? `${origin}${raw.startsWith('/') ? '' : '/'}${raw}`
                      : null;
                return (
                  <Link key={brand.id || brand.slug || brand.name} to={`/shop/brand/${brand.slug}`} className="brand-item" title={brand.name}>
                    {imgSrc ? (
                      <img src={imgSrc} alt={brand.name} />
                    ) : (
                      <span className="brand-name-fallback">{brand.name}</span>
                    )}
                  </Link>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* Customer Favorites */}
      <section className="products-section">
        <div className="container">
          <h2 className="section-title">Customer Favorites</h2>
          <div className="products-grid">
            {customerFavorites.length === 0 && !shopProductsError ? (
              <p className="category-hint" style={{ gridColumn: '1 / -1' }}>
                More products will appear here as our catalog grows.
              </p>
            ) : (
              customerFavorites.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))
            )}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="testimonial-section">
        <div className="container">
          <div className="testimonial-content">
            <div className="testimonial-logo">
              <img src="https://via.placeholder.com/150x50?text=Trustpilot" alt="Trustpilot" />
              <div className="stars">★★★★★</div>
            </div>
            <div className="testimonial-quote">
              <p>"World Class! We love doing business with Chesi! Their staff is always friendly and knowledgeable, and willing to understand their customer's true needs in order to find the appropriate product(s) for their unique circumstances. Their pricing is very competitive, and shipping times are second to none. We are pleased with the partnership and will continue doing business with them for many years to come."</p>
              <p className="testimonial-author">- BENJAMIN</p>
            </div>
            <div className="testimonial-image">
              <img src="https://via.placeholder.com/200x200?text=Customer" alt="Customer" />
            </div>
          </div>
        </div>
      </section>

      {/* Top Customers — same logos as home page (CMS → Client Logos) */}
      {(clientLogosLoading || clientLogos.length > 0) && (
        <section className="top-customers-section">
          <div className="container">
            <h2 className="section-title">OUR TOP CUSTOMERS</h2>
            <div className="customers-grid">
              {clientLogosLoading &&
                Array.from({ length: 6 }).map((_, index) => (
                  <div key={`client-skeleton-${index}`} className="customer-item customer-item--skeleton" aria-hidden="true" />
                ))}

              {!clientLogosLoading &&
                clientLogos.map((logo) => {
                  const imageUrl = resolveImageUrl(logo.logo_url, logo.name);

                  if (logo.website_url) {
                    return (
                      <a
                        key={logo.id}
                        href={logo.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="customer-item-link"
                        aria-label={logo.name}
                      >
                        <div className="customer-item">
                          <img src={imageUrl} alt={logo.name} loading="lazy" decoding="async" />
                        </div>
                      </a>
                    );
                  }

                  return (
                    <div key={logo.id} className="customer-item">
                      <img src={imageUrl} alt={logo.name} loading="lazy" decoding="async" />
                    </div>
                  );
                })}
            </div>
          </div>
        </section>
      )}

      {/* Free Tools */}
      <section className="tools-section">
        <div className="container">
          <h2 className="section-title">free tools</h2>
          <div className="tools-grid">
            <div className="tool-item">
              <div className="tool-icon">🏷️</div>
              <h3>Custom Barcode Labels</h3>
            </div>
            <div className="tool-item">
              <div className="tool-icon">📋</div>
              <h3>Preprinted Barcode Labels</h3>
            </div>
            <div className="tool-item">
              <div className="tool-icon">🔧</div>
              <h3>Free Barcode Generator</h3>
            </div>
            <div className="tool-item">
              <div className="tool-icon">🔤</div>
              <h3>Free Barcode Font</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="shop-footer">
        <div className="footer-top">
          <div className="container">
            <div className="footer-contact">
              <div className="contact-item">
                <span className="contact-icon">💬</span>
                <span>CHAT</span>
              </div>
              <div className="contact-item">
                <span className="contact-icon">✉️</span>
                <span>EMAIL US</span>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📞</span>
                <span>+254 727 450523</span>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Our Company</h4>
                <ul>
                  <li><a href="#">About Us</a></li>
                  <li><a href="#">Careers</a></li>
                  <li><a href="#">Contact</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Help & Support</h4>
                <ul>
                  <li><a href="#">FAQs</a></li>
                  <li><a href="#">Shipping</a></li>
                  <li><a href="#">Returns</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Services & Solutions</h4>
                <ul>
                  <li><a href="#">Custom Solutions</a></li>
                  <li><a href="#">Technical Support</a></li>
                  <li><a href="#">Training</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Shop Now</h4>
                <ul>
                  <li><a href="#">All Products</a></li>
                  <li><a href="#">New Arrivals</a></li>
                  <li><a href="#">Sale Items</a></li>
                </ul>
              </div>
            </div>
            <div className="footer-newsletter">
              <h4>Stay Informed</h4>
              <div className="newsletter-form">
                <input type="email" placeholder="Enter your email" />
                <button>SUBMIT</button>
              </div>
            </div>
            <div className="footer-social">
              <a href="#">LinkedIn</a>
              <a href="#">Twitter</a>
              <a href="#">Facebook</a>
              <a href="#">YouTube</a>
            </div>
            <div className="footer-copyright">
              <p>&copy; 2025 Chesi Mobile Solutions. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Shop;

